/**
 * @file useFontSample.ts
 * @description Подгрузка образца шрифта в кабинет (phase19.16 Р1, Р1-7;
 * uiux-design-r1 §1, §11 риск 2 — «файлы грузятся только для видимых
 * строк»).
 *
 * Шрифт платформы — файл с витрины магазина (`<сайт>/fonts/<key>/…woff2`,
 * адрес строит `siteFontFileUrl` пакета shared — тот же, что у витрины):
 * шрифты платформы лежат на её сервере, без сторонних сервисов (SB10).
 * Свой шрифт магазина (`shop:<id>`, SB99) — файл начертания из реестра
 * (`files[].url` — путь от корня API, при другом origin API приклеивается
 * к нему, как у картинок медиатеки): обычный прямой или ближайший по весу.
 * Загрузка через `FontFace` под своим именем семейства
 * (`commora-sample-<key>-<вес>`), чтобы образец не зависел от шрифтов
 * кабинета. Повторно файл не грузится — результат запоминается на всё
 * время работы вкладки (ключ — адрес файла).
 */

import {
  isShopFontEntry,
  nearestFontWeight,
  PLATFORM_FONT_REGISTRY,
  siteFontFileUrl,
  type SiteFontRegistry,
} from "@chaiberi/shared";
import { useEffect, useState } from "react";

import { API_URL } from "../../lib/api-client";
import { cabinetFontFileUrl } from "../../lib/site-view/shop-fonts";

export type FontSampleStatus = "system" | "loading" | "ready" | "error";

/** Загруженные и загружаемые образцы: ключ «адрес файла:вес». */
const cache = new Map<string, Promise<boolean>>();

/** Имя семейства образца в кабинете (только [a-z0-9-] — ключ `shop:` с двоеточием). */
export function sampleFamily(key: string, weight: number): string {
  return `commora-sample-${key.replace(/[^a-z0-9-]/gi, "-")}-${weight}`;
}

function loadSample(key: string, weight: number, url: string): Promise<boolean> {
  const id = `${url}:${weight}`;
  const existing = cache.get(id);
  if (existing) return existing;
  const promise = (async () => {
    if (typeof FontFace === "undefined" || !document.fonts) return false;
    try {
      const face = new FontFace(sampleFamily(key, weight), `url("${url}") format("woff2")`, { weight: String(weight) });
      document.fonts.add(await face.load());
      return true;
    } catch {
      return false;
    }
  })();
  cache.set(id, promise);
  return promise;
}

/**
 * Адрес файла образца: свой шрифт — прямое начертание ближайшего веса
 * (нет прямого — любое), шрифт платформы — файл витрины; `null` — грузить
 * нечего (системный шрифт или нет адреса витрины).
 */
export function sampleUrl(
  key: string,
  weight: number,
  siteUrl: string | null,
  registry: SiteFontRegistry,
  apiBase: string = API_URL,
): string | null {
  const entry = registry.find(key);
  if (!entry) return null;
  if (isShopFontEntry(entry)) {
    const normal = entry.files.filter((f) => f.style === "normal");
    const pool = normal.length > 0 ? normal : entry.files;
    const url = pool.find((f) => f.weight === weight)?.url ?? pool[0]?.url;
    return url ? cabinetFontFileUrl(url, apiBase) : null;
  }
  if (entry.license === "system" || !siteUrl) return null;
  return siteFontFileUrl(key, "cyrillic", weight, `${siteUrl.replace(/\/+$/, "")}/fonts`);
}

export interface FontSample {
  status: FontSampleStatus;
  /** Готовый `font-family` для образца (системный, пока файл не загружен). */
  fontFamily: string;
  weight: number;
}

/**
 * Образец шрифта: `enabled` — строка видна (иначе файл не грузится);
 * `siteUrl` — адрес витрины, с которой берутся файлы платформы;
 * `registry` — каталог платформы + свои шрифты магазина.
 */
export function useFontSample(
  key: string,
  weight: number,
  enabled: boolean,
  siteUrl: string | null,
  registry: SiteFontRegistry = PLATFORM_FONT_REGISTRY,
): FontSample {
  const entry = registry.find(key);
  const w = nearestFontWeight(key, weight, registry);
  const isSystem = !entry || (!isShopFontEntry(entry) && entry.license === "system");
  const url = sampleUrl(key, w, siteUrl, registry);
  const [status, setStatus] = useState<FontSampleStatus>(isSystem ? "system" : "loading");

  useEffect(() => {
    if (isSystem) {
      setStatus("system");
      return;
    }
    if (!enabled) return;
    if (!url) {
      setStatus("error");
      return;
    }
    let cancelled = false;
    setStatus("loading");
    void loadSample(key, w, url).then((ok) => {
      if (!cancelled) setStatus(ok ? "ready" : "error");
    });
    return () => {
      cancelled = true;
    };
  }, [key, w, enabled, url, isSystem]);

  const fallback = 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';
  return {
    status,
    fontFamily: status === "ready" ? `"${sampleFamily(key, w)}", ${fallback}` : fallback,
    weight: w,
  };
}
